/** Progressive resistance: additional finger travel gives less movement, capped at 48pt. */
export function resistTopSheetOverdrag(value: number, minimum: number, maximum: number) {
  "worklet";
  const edge = Math.max(minimum, Math.min(maximum, value));
  const distance = value - edge;
  return edge + (distance * 0.55) / (1 + (Math.abs(distance) * 0.55) / 48);
}

/** Recover finger travel when grabbing a sheet during its spring-back. */
export function topSheetDragOrigin(value: number, minimum: number, maximum: number) {
  "worklet";
  const edge = Math.max(minimum, Math.min(maximum, value));
  const distance = value - edge;
  return edge + distance / (0.55 * Math.max(0.01, 1 - Math.abs(distance) / 48));
}

/** Gorhom 5's default handle resistance, reflected onto an anchored top sheet's height. */
export function resistGorhomSheetOverdrag(value: number, minimum: number, maximum: number) {
  "worklet";
  const edge = Math.max(minimum, Math.min(maximum, value));
  const distance = value - edge;
  return distance === 0
    ? value
    : edge + Math.sign(distance) * Math.sqrt(1 + Math.abs(distance)) * 2.5;
}

export function gorhomSheetDragOrigin(value: number, minimum: number, maximum: number) {
  "worklet";
  const edge = Math.max(minimum, Math.min(maximum, value));
  const distance = value - edge;
  return distance === 0
    ? value
    : edge + Math.sign(distance) * Math.max(0, (distance / 2.5) ** 2 - 1);
}

/** Gorhom's snapPoint utility projects raw finger travel by 0.2 seconds of release velocity. */
export function gorhomSheetSnapPosition(
  value: number,
  velocity: number,
  minimum: number,
  maximum: number,
): 0 | 1 {
  "worklet";
  const projected = value + velocity * 0.2;
  return Math.abs(projected - minimum) <= Math.abs(projected - maximum) ? 0 : 1;
}

export function topSheetSnapPosition(
  value: number,
  velocity: number,
  minimum: number,
  maximum: number,
  threshold = 0.5,
): 0 | 1 {
  "worklet";
  if (maximum <= minimum || value <= minimum) {
    return 0;
  }
  if (value >= maximum) {
    return 1;
  }
  return value + velocity * 0.12 > minimum + (maximum - minimum) * threshold ? 1 : 0;
}

/**
 * `topSheetSnapPosition` over any number of resting positions (ascending): a release past either
 * end rests there, and one between them rests at the position nearest its projected travel.
 */
export function topSheetSnapDetent(value: number, velocity: number, detents: readonly number[]) {
  "worklet";
  const first = detents[0] ?? value;
  const last = detents.at(-1) ?? value;
  if (value <= first) {
    return first;
  }
  if (value >= last) {
    return last;
  }
  const projected = value + velocity * 0.12;
  let nearest = first;
  for (const detent of detents) {
    if (Math.abs(projected - detent) < Math.abs(projected - nearest)) {
      nearest = detent;
    }
  }
  return nearest;
}

/**
 * Sheet heights of the content boundaries that fit the viewport after the content scrolls, in
 * ascending order. A positive `interval` continues the last boundary past the end of the content,
 * so a body too short to reach the maximum still stops where its next rows would sit.
 * `includeMinimum` keeps the minimum a stop too, wherever the content has scrolled, for a sheet
 * that closes onto a strip above its content.
 */
export function topSheetSnapHeights(
  snapPoints: readonly number[],
  minimum: number,
  maximum: number,
  scrollOffset = 0,
  interval = 0,
  includeMinimum = false,
) {
  "worklet";
  const heights: number[] = includeMinimum ? [minimum] : [];
  const add = (height: number) => {
    if (height >= minimum && height <= maximum && height !== heights.at(-1)) {
      heights.push(height);
    }
  };
  for (const point of snapPoints) {
    add(point - scrollOffset);
  }
  const last = snapPoints.at(-1);
  if (last !== undefined && interval > 0) {
    for (let height = last - scrollOffset + interval; height <= maximum; height += interval) {
      add(height);
    }
  }
  return heights;
}

/** Snap only to content boundaries that fit the viewport, including after the content scrolls. */
export function topSheetSnapHeight(
  value: number,
  velocity: number,
  minimum: number,
  maximum: number,
  snapPoints: readonly number[],
  scrollOffset = 0,
  projection = 0.12,
  interval = 0,
  includeMinimum = false,
) {
  "worklet";
  const projected = Math.max(minimum, Math.min(maximum, value + velocity * projection));
  let nearest = projected;
  let distance = Infinity;
  for (const height of topSheetSnapHeights(
    snapPoints,
    minimum,
    maximum,
    scrollOffset,
    interval,
    includeMinimum,
  )) {
    const nextDistance = Math.abs(projected - height);
    if (nextDistance < distance) {
      nearest = height;
      distance = nextDistance;
    }
  }
  return nearest;
}
/**
 * Only the closed/header/max boundaries snap; every height above the header stays put.
 * `edgeDistance` is how far the releasing finger is from the sheet's anchored edge (its window y
 * for a top sheet).
 */
export function topSheetRevealRestHeight(
  height: number,
  headerHeight: number,
  maximum: number,
  edgeDistance = height,
) {
  "worklet";
  // A small edge allowance lets a finger reach the edge without requiring subpixel precision.
  if (height <= 8 || edgeDistance <= 8) {
    return 0;
  }
  return Math.min(maximum, Math.max(headerHeight, height));
}
